import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Brain, CreditCard, FileCheck, LogOut, Sparkles, MapPin, Info, RefreshCw, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [careerMappingPaid, setCareerMappingPaid] = useState(user?.career_mapping_paid || false);
  const [refreshing, setRefreshing] = useState(false);
  const fetchedRef = useRef(false);

  // Initialize from localStorage if user is null
  useEffect(() => {
    if (!user) {
      const cachedUser = localStorage.getItem('user');
      if (cachedUser) {
        const parsedUser = JSON.parse(cachedUser);
        setUser(parsedUser);
        setCareerMappingPaid(parsedUser.career_mapping_paid || false);
      } else {
        navigate('/auth');
      }
    }
  }, [user, setUser, navigate]);

  // Fetch fresh user data from server (only once)
  useEffect(() => {
    if (fetchedRef.current || !user) return;
    fetchedRef.current = true;

    const fetchFreshUserData = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;

      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const freshUserData = response.data;
        
        // Only update if payment status changed
        if (freshUserData.career_mapping_paid !== user.career_mapping_paid) {
          localStorage.setItem('user', JSON.stringify(freshUserData));
          setUser(freshUserData);
          setCareerMappingPaid(freshUserData.career_mapping_paid || false);
        } else {
          setCareerMappingPaid(freshUserData.career_mapping_paid || false);
        }
      } catch (error) {
        console.error('Failed to fetch fresh user data:', error);
      }
    };

    fetchFreshUserData();
  }, [user, setUser]);

  // Manual refresh function
  const refreshPaymentStatus = async () => {
    setRefreshing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const freshUserData = response.data;
      localStorage.setItem('user', JSON.stringify(freshUserData));
      setUser(freshUserData);
      setCareerMappingPaid(freshUserData.career_mapping_paid || false);
    } catch (error) {
      console.error('Failed to refresh:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    navigate('/');
  };

  // Check if user is eligible for career mapping (grade 10-12)
  const isEligibleForCareerMapping = user && ['10', '11', '12'].includes(user.grade);

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-xl border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Brain className="w-8 h-8 text-indigo-500" />
              <h1 className="font-heading text-2xl font-bold text-slate-900">PsychTest</h1>
            </div>
            <div className="hidden sm:flex items-center gap-1 text-xs text-slate-500 border-l border-slate-200 pl-3">
              <Sparkles className="w-3 h-3" />
              <span>Powered by</span>
              <span className="font-semibold text-indigo-600">Uxima Foundation</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              data-testid="about-uxima-btn"
              onClick={() => navigate('/about')}
              variant="ghost"
              className="flex items-center gap-2 text-slate-600"
            >
              <Info className="w-4 h-4" />
              About Us
            </Button>
            <Button
              data-testid="logout-btn"
              onClick={handleLogout}
              variant="ghost"
              className="flex items-center gap-2"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </Button>
          </div>
        </div>
      </nav>

      {/* Dashboard Content */}
      <div className="max-w-5xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-2">
            Welcome, {user.name}! 👋
          </h1>
          <p className="text-lg text-slate-600">
            Grade {user.grade === 'graduation' ? 'Graduation' : user.grade}
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {/* Psychometric Test Card - FREE */}
          <Card
            data-testid="test-access-card"
            className="bg-white rounded-2xl border border-teal-200 bg-teal-50/30 shadow-sm p-6"
          >
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                <FileCheck className="w-8 h-8 text-teal-500" />
                <CardTitle className="font-heading text-xl">Psychometric Test</CardTitle>
              </div>
              <span className="inline-block px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-xs font-semibold">
                FREE
              </span>
            </CardHeader>
            <CardContent>
              <p className="text-slate-600 text-sm mb-4">
                20 questions | Detailed insights on your strengths & areas for improvement
              </p>
              <Button
                data-testid="start-test-btn"
                onClick={() => navigate('/test')}
                className="rounded-full px-6 py-2 bg-teal-500 hover:bg-teal-600 text-white font-semibold transition-all hover:scale-105 shadow-lg shadow-teal-500/20"
              >
                Start Free Test
              </Button>
            </CardContent>
          </Card>

          {/* Career Mapping Card - PAID for grades 10-12 */}
          {isEligibleForCareerMapping && (
            <Card
              data-testid="counseling-card"
              className={`bg-white rounded-2xl border ${
                careerMappingPaid ? 'border-violet-200 bg-violet-50/30' : 'border-amber-200 bg-amber-50/30'
              } shadow-sm p-6`}
            >
              <CardHeader>
                <div className="flex items-center gap-3 mb-2">
                  <MapPin className="w-8 h-8 text-violet-500" />
                  <CardTitle className="font-heading text-xl">Career Mapping</CardTitle>
                </div>
                {!careerMappingPaid && (
                  <span className="inline-block px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-semibold">
                    ₹199
                  </span>
                )}
                {careerMappingPaid && (
                  <span className="inline-block px-3 py-1 bg-violet-100 text-violet-700 rounded-full text-xs font-semibold">
                    UNLOCKED
                  </span>
                )}
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 text-sm mb-3">
                  Get personalized career guidance with detailed report and career counseling by <strong>Dr. Joy Sardar</strong> (Ph.D Education)
                </p>
                {careerMappingPaid ? (
                  <Button
                    data-testid="start-counseling-btn"
                    onClick={() => navigate('/counseling')}
                    className="rounded-full px-6 py-2 bg-violet-500 hover:bg-violet-600 text-white font-semibold transition-all hover:scale-105 shadow-lg shadow-violet-500/20"
                  >
                    Start Career Mapping
                  </Button>
                ) : (
                  <Button
                    data-testid="unlock-career-mapping-btn"
                    onClick={() => navigate('/payment?type=career_mapping')}
                    className="rounded-full px-6 py-2 bg-amber-500 hover:bg-amber-600 text-white font-semibold transition-all hover:scale-105 shadow-lg shadow-amber-500/20"
                  >
                    Unlock for ₹199
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

          {/* About Uxima Foundation Card */}
          <Card
            data-testid="about-card"
            className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6"
          >
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                <Sparkles className="w-8 h-8 text-indigo-500" />
                <CardTitle className="font-heading text-xl">Uxima Foundation</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-slate-600 text-sm mb-4">
                Educate | Heal | Empower - Learn about our mission and initiatives
              </p>
              <Button
                data-testid="learn-more-btn"
                onClick={() => navigate('/about')}
                variant="outline"
                className="rounded-full px-6 py-2 border-indigo-200 text-indigo-600 hover:bg-indigo-50 font-semibold"
              >
                Learn More
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Instructions Card */}
        <Card className="mt-6 bg-white/80 backdrop-blur-md rounded-2xl border border-slate-100 shadow-sm">
          <CardHeader>
            <CardTitle className="font-heading text-xl">About the Free Psychometric Test</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-slate-600">
              <li className="flex items-start gap-2">
                <span className="text-teal-500 font-bold">•</span>
                20 questions covering logical, numerical, verbal, and spatial reasoning
              </li>
              <li className="flex items-start gap-2">
                <span className="text-teal-500 font-bold">•</span>
                Receive detailed insights on your cognitive strengths and areas for improvement
              </li>
              <li className="flex items-start gap-2">
                <span className="text-teal-500 font-bold">•</span>
                Personalized recommendations based on your performance
              </li>
              <li className="flex items-start gap-2">
                <span className="text-teal-500 font-bold">•</span>
                No time limit - take your time to answer thoughtfully
              </li>
              <li className="flex items-start gap-2">
                <span className="text-teal-500 font-bold">•</span>
                100% free - no payment required
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Career Mapping Info for eligible students */}
        {isEligibleForCareerMapping && (
          <Card className="mt-6 bg-gradient-to-r from-violet-50 to-indigo-50 rounded-2xl border border-violet-200 shadow-sm">
            <CardHeader>
              <CardTitle className="font-heading text-xl text-violet-900">Career Mapping Program</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-700 mb-4">
                As a student in Grade {user.grade}, you're eligible for our comprehensive Career Mapping program designed to help you make informed decisions about your future.
              </p>
              <ul className="space-y-2 text-slate-600 mb-4">
                <li className="flex items-start gap-2">
                  <span className="text-violet-500 font-bold">•</span>
                  90-question comprehensive assessment to understand your aptitude and personality
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-violet-500 font-bold">•</span>
                  Detailed report with personalized 30-day action plan
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-violet-500 font-bold">•</span>
                  Career counseling by Dr. Joy Sardar (Ph.D Education)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-violet-500 font-bold">•</span>
                  Stream selection guidance (Science/Commerce/Humanities)
                </li>
              </ul>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
